import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import {
    NativeSyntheticEvent,
    TextInput,
    TextInputSubmitEditingEventData,
    View,
} from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import CloseIcon from 'assets/images/svgs/ico_close.svg';
import SearchIcon from 'assets/images/svgs/ico_search.svg';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import {
    SEARCH_ICON_ID,
    SEARCH_INPUT_ID,
    SEARCH_X_ICON_ID,
} from 'config/testID';

import { selectScreenReaderEnabled } from 'store/config/selectors';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';

import { lightGrey } from 'utils/colors';
import { isWeb } from 'utils/responsiveness';

import SvgButton from 'components/CustomButtons/SvgButton';

import styles from './styles';

type Props = {
    searchText: string;
    onSubmitEditing(
        e: NativeSyntheticEvent<TextInputSubmitEditingEventData>,
    ): void;
    onChangeSearchText(text: string): void;
};

const { SCREEN_SEARCH, ACCESSIBILITY } = LANGUAGE_KEYS;

function SearchInput({
    searchText,
    onSubmitEditing,
    onChangeSearchText,
}: Props) {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);

    const handleClosePress = useCallback(() => {
        onChangeSearchText('');
    }, [onChangeSearchText]);

    const accessibilityHint = useMemo(() => {
        return screenReaderEnabled
            ? `${t(ACCESSIBILITY.SEARCH.ACCESSIBILITY_SEARCH_HINT_RN).replace(
                  '%SCREEN_SEARCH%',
                  t(SCREEN_SEARCH),
              )}`
            : `${t(ACCESSIBILITY.SEARCH.ACCESSIBILITY_SEARCH_HINT_RN).replace(
                  '%SCREEN_SEARCH%',
                  '',
              )}`;
    }, [screenReaderEnabled, t]);

    const placeholder = useMemo(() => {
        return screenReaderEnabled ? undefined : t(SCREEN_SEARCH);
    }, [screenReaderEnabled, t]);

    useEffect(() => {
        if (isWeb) {
            setTimeout(() => {
                setAccessibilityFocusTo(inputRef);
            }, 50);
        }
    }, []);

    return (
        <View style={styles.textInputContainer}>
            <View style={styles.searchInputLeftControls}>
                <SearchIcon
                    testID={SEARCH_ICON_ID}
                    style={styles.searchIconSvgStyle}
                    aria-hidden={isWeb}
                />
                <View style={styles.inputContainer}>
                    <TextInput
                        ref={inputRef}
                        testID={SEARCH_INPUT_ID}
                        autoFocus
                        autoCorrect={false}
                        value={searchText}
                        style={styles.textInput}
                        placeholderTextColor={lightGrey(0.5)}
                        returnKeyType="search"
                        placeholder={placeholder}
                        accessibilityLabel={accessibilityHint}
                        accessibilityRole="search"
                        role="searchbox"
                        onChangeText={onChangeSearchText}
                        onSubmitEditing={onSubmitEditing}
                    />
                </View>
            </View>
            {!!searchText && (
                <SvgButton
                    testID={SEARCH_X_ICON_ID}
                    Svg={CloseIcon}
                    style={styles.closeIconSvgStyle}
                    svgStyle={styles.closeIcon}
                    onPress={handleClosePress}
                    accessibilityLabel={t(
                        ACCESSIBILITY.SEARCH.ACCESSIBILITY_SEARCH_X_CLEAR_ICON,
                    )}
                    accessibilityRole="button"
                />
            )}
        </View>
    );
}

export default SearchInput;
